import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

export type NotificationType =
  | 'transfer_request'
  | 'transfer_approved'
  | 'transfer_rejected'
  | 'member_status_change'
  | 'census_deadline'
  | 'system_alert'
  | 'event_registration'
  | 'support_comment'
  | 'classified_pending'
  | 'classified_approved'
  | 'classified_declined'
  | 'classified_removed'
  | 'supplier_pending'
  | 'supplier_approved'
  | 'supplier_rejected'
  | 'access_invite_issued'
  | 'access_invite_cancelled'
  | 'access_invite_expiring'
  | 'gate_entry'
  | 'gate_exit'
  | 'gate_sos'
  | 'facility_booking_reminder'
  | 'facility_booking_cancelled'
  | 'facility_booking_approved'
  | 'facility_booking_rejected'
  | 'facility_booking_updated'
  | 'facility_terms_updated'
  | 'general';

export interface Notification {
  notification_id: string;
  title: string;
  message: string;
  notification_type: NotificationType;
  is_read: boolean;
  created_at: string;
  read_at?: string;
  priority?: 'low' | 'medium' | 'high';
  action_url?: string;
  related_id?: string;
  // Deep-link metadata (mirrors the push data payload) — drives routing + tenant switch.
  tenant_id?: string;
  module?: string;
  related_entity_type?: string;
  metadata?: Record<string, unknown> | null;
}

export interface NotificationFilters {
  is_read?: boolean;
  limit?: number;
  offset?: number;
}

export async function fetchNotifications(
  filters: NotificationFilters = {},
): Promise<Notification[]> {
  const { data } = await tenantApi.get('/notifications', { params: filters });
  return unwrapList<Notification>(data);
}

export async function fetchUnreadNotificationCount(): Promise<number> {
  const { data } = await tenantApi.get('/notifications/unread-count');
  const payload = unwrap<{ unread_count: number }>(data);
  return Number(payload.unread_count) || 0;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await tenantApi.put(`/notifications/${notificationId}/read`, {});
}

export async function deleteNotification(notificationId: string): Promise<void> {
  await tenantApi.delete(`/notifications/${notificationId}`);
}

/**
 * Clear (delete) every notification the member owns in a single server-side
 * operation. This removes ALL of them, not just the currently-loaded page, so
 * nothing is left behind. Backed by DELETE /notifications/clear-all.
 */
export async function clearAllNotifications(): Promise<void> {
  await tenantApi.delete('/notifications/clear-all');
}

export interface RegisterPushTokenInput {
  token: string;
  platform: string;
  app_id?: string;
  device_id?: string | null;
}

export async function registerPushToken(input: RegisterPushTokenInput): Promise<void> {
  await tenantApi.post('/notifications/push-token', input);
}

export async function unregisterPushToken(token: string): Promise<void> {
  await tenantApi.delete('/notifications/push-token', { data: { token } });
}
